import {
  CompanyRoleIdWithName,
  DecisionContext,
  toCompanyRoleIdWithName,
} from "@/dbot/common/commonDomain";
import { makeAutoObservable } from "mobx";
import { CompanyRole } from "@/types/CompanyInfo";
import { identity } from "lodash";
import { sortedBoard } from "@/utils/Roles";
import { DecisionType } from "@/common/decisions";
import { BooleanFieldModel, OptionFieldModel, StringFieldModel } from "@/common/commonForms";
import { TMessage } from "@/i18n/tt";

export enum SignatureAssignment {
  chairpersonAlone = "chairpersonAlone",
  chairpersonAndMember = "chairpersonAndMember",
  twoMembers = "twoMembers",
  everyMember = "everyMember",
  ceoAlone = "ceoAlone",
  namedPersons = "namedPersons",
  namedPersonsJointly = "namedPersonsJointly",
  other = "other",
}

export type BoardAssignsSignature = {
  type: DecisionType.boardAssignsSignature;
  signatureAssignedTo: Array<SignatureAssignment>;
  signatureAssignedToNamedBoardMembers: Array<CompanyRoleIdWithName>;
  signatureAssignedToNamedPersons: string;
  signatureAssignedToNamedBoardMembersJointly: Array<CompanyRoleIdWithName>;
  signatureAssignedToNamedPersonsJointly: string;
  signatureAssignedOtherRegulation: string;
  revokeBoardDecisionsSignatures: boolean;
};

export class BoardAssignsSignatureForm {
  readonly type = DecisionType.boardAssignsSignature;
  signatureAssignedTo: OptionFieldModel<SignatureAssignment>;
  signatureAssignedToNamedBoardMembers: OptionFieldModel<CompanyRoleIdWithName>;
  signatureAssignedToNamedPersons: StringFieldModel;
  signatureAssignedToNamedBoardMembersJointly: OptionFieldModel<CompanyRoleIdWithName>;
  signatureAssignedToNamedPersonsJointly: StringFieldModel;
  signatureAssignedOtherRegulation: StringFieldModel;
  revokeBoardDecisionsSignatures: BooleanFieldModel;

  constructor(
    private context: DecisionContext<BoardAssignsSignature>,
    public data?: BoardAssignsSignature,
  ) {
    this.signatureAssignedTo = new OptionFieldModel({
      value: data?.signatureAssignedTo,
      options: Object.values(SignatureAssignment),
      required: true,
      idFn: identity,
      labelFn: (x) => this.decisionsSignatureAssignedTo(x),
      onChange: () => {
        this.signatureAssignedOtherRegulation.setRequired(
          this.signatureAssignedTo.selected.includes(SignatureAssignment.other),
        );
      },
    });
    this.signatureAssignedToNamedPersons = new StringFieldModel({
      value: data?.signatureAssignedToNamedPersons || "",
    });
    this.signatureAssignedToNamedBoardMembers = new OptionFieldModel({
      value: data?.signatureAssignedToNamedBoardMembers,
      options: this.boardMembers.map(toCompanyRoleIdWithName),
      idFn: (r) => r.companyroleId.toString(),
      labelFn: (r) => ({ translated: r.name }),
    });
    this.signatureAssignedToNamedPersonsJointly = new StringFieldModel({
      value: data?.signatureAssignedToNamedPersonsJointly || "",
    });
    this.signatureAssignedToNamedBoardMembersJointly = new OptionFieldModel({
      value: data?.signatureAssignedToNamedBoardMembersJointly,
      options: this.boardMembers.map(toCompanyRoleIdWithName),
      idFn: (r) => r.companyroleId.toString(),
      labelFn: (r) => ({ translated: r.name }),
    });
    this.signatureAssignedOtherRegulation = new StringFieldModel({
      value: data?.signatureAssignedOtherRegulation || "",
    });
    this.revokeBoardDecisionsSignatures = new BooleanFieldModel({
      value: data?.revokeBoardDecisionsSignatures,
      required: true,
    });
    makeAutoObservable(this);
  }

  get isProjectLocked(): boolean {
    return this.context.isLocked;
  }

  get isNamedPersonsValid() {
    const isNamedPerson = this.signatureAssignedTo.selected.includes(
      SignatureAssignment.namedPersons,
    );
    return (
      !isNamedPerson ||
      (isNamedPerson &&
        (!this.signatureAssignedToNamedPersons.isEmpty ||
          !this.signatureAssignedToNamedBoardMembers.isNoneSelected))
    );
  }

  get isNamedPersonsJointlyValid() {
    return (
      !this.signatureAssignedTo.selected.includes(SignatureAssignment.namedPersonsJointly) ||
      !this.signatureAssignedToNamedPersonsJointly.isEmpty ||
      !this.signatureAssignedToNamedBoardMembersJointly.isNoneSelected
    );
  }

  get isValid() {
    return (
      this.signatureAssignedTo.isValid &&
      this.isNamedPersonsValid &&
      this.isNamedPersonsJointlyValid &&
      this.signatureAssignedOtherRegulation.isValid &&
      this.revokeBoardDecisionsSignatures.isValid
    );
  }

  get isShowNamedPersonInput() {
    return this.signatureAssignedTo?.isSelected(SignatureAssignment.namedPersons);
  }

  get isShowNamedPersonJointlyInput() {
    return this.signatureAssignedTo?.isSelected(SignatureAssignment.namedPersonsJointly);
  }

  get isShowOtherRegulationsInput() {
    return this.signatureAssignedTo?.isSelected(SignatureAssignment.other);
  }

  get boardMembers(): CompanyRole[] {
    return sortedBoard(this.context.initialState.roles);
  }

  decisionsSignatureAssignedTo(x: SignatureAssignment): TMessage {
    return {
      key: `decisions.signature.assignedTo.${x}`,
    };
  }

  validate(): boolean {
    this.signatureAssignedTo.validate();
    this.signatureAssignedToNamedPersons.validate();
    this.signatureAssignedToNamedBoardMembers.validate();
    this.signatureAssignedToNamedPersonsJointly.validate();
    this.signatureAssignedToNamedBoardMembersJointly.validate();
    this.signatureAssignedOtherRegulation.validate();
    this.revokeBoardDecisionsSignatures.validate();
    return this.isValid;
  }
}
